import type { ReactNode } from 'react';
import Link from 'next/link';
import type { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import NameCell from '@/components/publicSearch/resultCell/NameCell';
import { HyperLink } from '@/components/publicSearch/resultCell/HyperLink';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import {
  FieldOption,
  GetMembersRow,
} from '@/components/publicSearch/constants';
import { displayPlainText, isImgOnlyHtml } from '@/utils/stripHtmlForTooltip';
import { Ilr2StructuredImgCell } from '@/components/publicSearch/Ilr2StructuredImgCell';
import { BreedplanCopBadge } from '@/components/publicSearch/member/BreedplanCopBadge';
import {
  getSocietyMemberField10Text,
  getSocietyMemberFieldNumber,
} from '@/components/publicSearch/member/societyMemberFieldValue';

function getMemberDetailPath(orgCode: string, memberId: number | string) {
  return `/${orgCode}/${ROUTE_SEGMENT.MEMBERS}/${memberId}`;
}

/** Circle avatar containing a person icon, used as the leading visual in a MemberListRow. */
function MemberIconBadge() {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: alpha(theme.palette.primary.main, 0.12),
        color: theme.palette.primary.main,
      }}
    >
      <PersonOutlineIcon sx={{ fontSize: 18 }} />
    </Box>
  );
}

/**
 * DataGrid cell renderer for member name/identity fields.
 * On desktop returns a plain `NameCell` link; on mobile renders a compact
 * card with Stud No., Stud Prefix, Herd Letters, and Postcode subtitle lines.
 */
function MemberListRow({
  orgCode,
  row,
}: {
  orgCode: string;
  row: GetMembersRow;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const memberId = row.MEMBER_ID ?? row.memberId;
  const name = displayPlainText(row.MEMB_NAME as string);
  const ident = (row.MEMBER_IDENT as string) ?? '';
  const prefix = (row.PREFIX as string) ?? '';

  if (!memberId) return null;

  const path = getMemberDetailPath(orgCode, memberId);
  const displayName = name || ident || 'Member';

  if (!isMobile) {
    return <NameCell name={displayName} path={path} />;
  }

  const herdLetters = (row['SORTNAME'] as string) ?? '';
  const postcode = (row['POSTCODE'] as string) ?? '';

  const line1Parts = [
    ident ? `Stud No. ${ident.trim()}` : null,
    prefix ? `Stud Prefix: ${prefix.trim()}` : null,
  ].filter(Boolean);
  const line2Parts = [
    herdLetters ? `Herd Letters: ${herdLetters.trim()}` : null,
    postcode ? `Postcode: ${postcode.trim()}` : null,
  ].filter(Boolean);

  const subtitleSx = {
    color: 'text.secondary',
    fontSize: '12px',
    lineHeight: 1.35,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as const;

  return (
    <Link
      href={path}
      prefetch={false}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'block',
        width: '100%',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          minWidth: 0,
          minHeight: 76,
          px: '14px',
          py: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          boxSizing: 'border-box',
        }}
      >
        <MemberIconBadge />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              color: 'info.main',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayName}
          </Typography>
          {line1Parts.length > 0 && (
            <Typography component="div" sx={subtitleSx}>
              {line1Parts.join(' · ')}
            </Typography>
          )}
          {line2Parts.length > 0 && (
            <Typography component="div" sx={subtitleSx}>
              {line2Parts.join(' · ')}
            </Typography>
          )}
        </Box>
        <ChevronRightIcon
          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
        />
      </Box>
    </Link>
  );
}

function resolveMemberId(
  row: GridRenderCellParams<GetMembersRow>['row'],
): number | undefined {
  const raw = row.memberId ?? row.MEMBER_ID;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function createRenderCell(field: keyof GetMembersRow, orgCode: string) {
  return function renderCell(params: GridRenderCellParams<GetMembersRow>) {
    const memberId = resolveMemberId(params.row);
    const value = params.row[field];

    if (field === FieldOption.memberName.value && orgCode && memberId) {
      return <MemberListRow orgCode={orgCode} row={params.row} />;
    }

    if (
      field === FieldOption.memberIdent.value &&
      orgCode &&
      memberId &&
      value
    ) {
      return (
        <HyperLink
          label={String(value)}
          pathname={getMemberDetailPath(orgCode, memberId)}
        />
      );
    }

    if (field === FieldOption.member.value && orgCode && memberId) {
      return <MemberListRow orgCode={orgCode} row={params.row} />;
    }

    if (value == null || value === '') return '';

    if (typeof value === 'string') {
      // Mirror the member detail page's society-field decoding (BLS-359 / BLS-375):
      // field 10 is an i6 JSON documents list even when used for plain text such as
      // a contact name; field 15 is a BREEDPLAN COP rating code rendered as a star logo.
      // Grid row keys are SOCIETY_MEMBER_FIELD_n (see mapMemberSettingFieldToRowKey).
      const societyFieldNumber = getSocietyMemberFieldNumber(String(field));
      if (societyFieldNumber === 10) {
        return displayPlainText(getSocietyMemberField10Text(value));
      }
      if (societyFieldNumber === 15) {
        return <BreedplanCopBadge value={value} />;
      }
      if (isImgOnlyHtml(value)) {
        return <Ilr2StructuredImgCell value={value} societyId={orgCode} />;
      }
      return displayPlainText(value);
    }

    return value as ReactNode;
  };
}
